/**
 * Waiting on a payment the donor completes somewhere else.
 *
 *  - Easypaisa: they approve on their own phone.
 *  - Stripe and JazzCash: they pay on the provider's own secure page.
 *
 * Either way there is nothing in this app to press "done", so the screen asks
 * the server every few seconds. The server asks the provider — or has already
 * been told, by a signed post-back — and verifies the answer. This screen only
 * reports what it is told and never decides an outcome.
 *
 * A donor returning from a hosted page arrives here with only the payment id,
 * so everything else is looked up.
 *
 * In the Easypaisa demo there is no phone, so the phone is two buttons here,
 * clearly marked. The server refuses them for any real payment.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Image, StyleSheet, View } from 'react-native';

import { formatMoney, type PaymentRecord } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { Glyph } from '../../components/common/Icon';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import { Text } from '../../components/common/Text';
import {
  useDemoPaymentDecision,
  useDonation,
  useInitiatePayment,
  usePayment,
  useRefreshPayment,
} from '../../core/api/hooks';
import { openHostedPage, paymentReturnUrl } from '../../core/payments/hostedPage';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

/** Often enough to feel immediate, rare enough not to hammer a provider. */
const POLL_MS = 3000;

/** Past every provider's own expiry; stop asking after this. */
const GIVE_UP_MS = 35 * 60 * 1000;

const LOGOS: Record<string, number> = {
  easypaisa: require('../../assets/payments/easypaisa.png'),
  jazzcash: require('../../assets/payments/jazzcash.png'),
};

export function PaymentPendingScreen({
  navigation,
  route,
}: DonorScreenProps<'PaymentPending'>) {
  const t = useT();
  const params = route.params;
  const { paymentId, redirectUrl, testMode, simulated, returned, cancelled } = params;

  // What the route did not bring — a donor back from a hosted page brings
  // only the id — comes from the server.
  const record = usePayment(paymentId);
  const method = params.method ?? record.data?.methodCode ?? '';
  const donationId = params.donationId ?? record.data?.donationId ?? null;
  // A tiffin order is paid through this same screen; the payment itself says
  // which kind of order it is, so a customer back from a hosted page still
  // lands on the tiffin confirmation rather than a donation's thank-you.
  const orderType = params.orderType ?? record.data?.orderType ?? null;
  const tiffin = orderType === 'TIFFIN';
  const donation = useDonation(
    params.publicReference || record.data?.publicReference || tiffin ? null : donationId,
  );
  const publicReference =
    params.publicReference ?? record.data?.publicReference ?? donation.data?.publicReference ?? null;
  const amount = params.amount ?? record.data?.amount ?? null;

  const finish = useCallback(() => {
    if (!donationId || !publicReference) return;
    // Replace, not push: Back from the receipt must not return here.
    if (tiffin) {
      navigation.replace('TiffinConfirmation', { orderId: donationId, publicReference });
    } else {
      navigation.replace('ThankYou', { donationId, publicReference });
    }
  }, [donationId, navigation, publicReference, tiffin]);

  const refresh = useRefreshPayment();
  const decide = useDemoPaymentDecision();
  const restart = useInitiatePayment();
  const [payment, setPayment] = useState<PaymentRecord | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const startedAt = useRef(Date.now());
  const opened = useRef(false);

  const hosted = method === 'card' || method === 'jazzcash';
  const current = payment ?? record.data ?? null;
  const failed = current?.status === 'FAILED';
  const label = current?.instrumentLabel ?? params.instrumentLabel ?? null;

  const settle = useCallback(
    (next: PaymentRecord) => {
      setPayment(next);
      if (next.status === 'PAID') finish();
    },
    [finish],
  );

  // A PAID answer that arrived before the order reference did.
  useEffect(() => {
    if (current?.status === 'PAID') finish();
  }, [current?.status, finish]);

  // Straight to the provider's page the first time, without an extra tap.
  useEffect(() => {
    if (redirectUrl && !returned && !opened.current) {
      opened.current = true;
      void openHostedPage(redirectUrl);
    }
  }, [redirectUrl, returned]);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      if (!active) return;
      if (Date.now() - startedAt.current > GIVE_UP_MS) {
        setGaveUp(true);
        return;
      }
      try {
        const next = await refresh.mutateAsync(paymentId);
        if (!active) return;
        settle(next);
        if (next.status === 'PAID' || next.status === 'FAILED') return;
      } catch {
        // A dropped connection is not a failed payment; keep asking.
      }
      timer = setTimeout(() => void poll(), POLL_MS);
    };

    // At once when coming back from a page — the answer is usually there.
    timer = setTimeout(() => void poll(), returned ? 0 : POLL_MS);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
    // Polling is started once per payment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  /**
   * A fresh page for the same donation and the same method. Needs nothing
   * but the donation id, which is the point: a donor back from a hosted page
   * arrives on a newly loaded site, with the checkout draft long gone.
   */
  const onTryAgain = async () => {
    if (!donationId || !method) return;
    try {
      const intent = await restart.mutateAsync({
        donationId,
        paymentMethodCode: method,
        returnUrl: paymentReturnUrl(),
      });
      navigation.replace('PaymentPending', {
        paymentId: intent.paymentId,
        method,
        orderType: orderType ?? undefined,
        donationId,
        publicReference: publicReference ?? undefined,
        amount: amount ?? undefined,
        instrumentLabel: intent.instrumentLabel,
        simulated: intent.simulated,
        redirectUrl: intent.redirectUrl,
        testMode: intent.testMode,
      });
    } catch {
      // Shown below from the mutation's own error.
    }
  };

  const onDecide = async (approve: boolean) => {
    try {
      settle(await decide.mutateAsync({ paymentId, approve }));
    } catch {
      // Shown below from the mutation's own error.
    }
  };

  const stopped = failed || Boolean(cancelled);
  const waiting = !stopped && !gaveUp;
  const title = failed
    ? t('Payment not completed')
    : cancelled
      ? t('Payment cancelled')
      : hosted
        ? t('Complete your payment')
        : t('Approve on your phone');
  const subtitle = failed
    ? t(current?.failureMessage ?? 'The payment was not completed.')
    : cancelled
      ? t('You left the payment page before paying. Nothing was charged.')
      : method === 'card'
        ? t("Pay on Stripe's secure page. This screen updates as soon as you have paid.")
        : method === 'jazzcash'
          ? t("Pay on JazzCash's secure page. This screen updates as soon as you have paid.")
          : t('Open the Easypaisa notification on your phone and enter your PIN.');

  return (
    <ScreenShell
      leading="back"
      onBackPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Home'))}
      testID="screen-payment-pending"
      subHeader={<ScreenTitle title={title} subtitle={subtitle} />}
      pinnedFooter={
        stopped || gaveUp ? (
          <View style={styles.footer}>
            {hosted && donationId ? (
              <Button
                label={t('Try again')}
                onPress={() => void onTryAgain()}
                loading={restart.isPending}
                testID="cta-payment-try-again"
              />
            ) : null}
            {/* Back from a hosted page the checkout draft is gone, so the
                way out is Home; otherwise the donor can pick another rail. */}
            {returned ? (
              <Button
                label={t('Back to Home')}
                variant={hosted ? 'secondary' : 'primary'}
                onPress={() => navigation.navigate('Home')}
                testID="cta-payment-home"
              />
            ) : (
              <Button
                label={t('Choose another way to pay')}
                variant={hosted && donationId ? 'secondary' : 'primary'}
                onPress={() => navigation.navigate(tiffin ? 'TiffinPayment' : 'Payment')}
                testID="cta-payment-retry"
              />
            )}
            {restart.error ? (
              <Text variant="caption" tone="danger" align="center">
                {t(restart.error.message)}
              </Text>
            ) : null}
          </View>
        ) : undefined
      }
    >
      <Card style={styles.card} testID="pending-summary">
        {LOGOS[method] ? (
          <Image
            source={LOGOS[method]}
            style={styles.logo}
            resizeMode="contain"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <View style={[styles.logo, styles.glyph]}>
            <Glyph name="card" size={32} />
          </View>
        )}
        <View style={styles.summary}>
          <Text variant="caption" tone="tertiary">{t('Total Amount')}</Text>
          <Text variant="amount" tone="brand" testID="pending-amount">
            {amount ? formatMoney(amount) : '—'}
          </Text>
          {label ? (
            <Text variant="label" tone="secondary" testID="pending-account">
              {label}
            </Text>
          ) : null}
        </View>
      </Card>

      {hosted && redirectUrl && waiting ? (
        <View style={styles.action}>
          <Button
            label={t('Open secure payment page')}
            onPress={() => void openHostedPage(redirectUrl)}
            variant="secondary"
            testID="cta-open-payment-page"
          />
        </View>
      ) : null}

      {method === 'card' && testMode && waiting ? (
        <View style={styles.test} testID="stripe-test-card">
          <Text variant="badge" tone="secondary">{t('TEST MODE')}</Text>
          <Text variant="body" align="center">
            {t('No real money moves. Pay with the test card 4242 4242 4242 4242, any future expiry date and any 3-digit CVC.')}
          </Text>
        </View>
      ) : null}

      {waiting ? (
        <View style={styles.waiting} testID="pending-waiting">
          <ActivityIndicator color={colors.text.brand} />
          <Text variant="body" tone="secondary" align="center">
            {hosted ? t('Waiting for the payment…') : t('Waiting for your approval…')}
          </Text>
        </View>
      ) : null}

      {gaveUp && !stopped ? (
        <Text variant="body" tone="secondary" align="center" style={styles.waiting}>
          {t('We have not heard back yet. If you paid, your donation will update shortly.')}
        </Text>
      ) : null}

      {simulated && waiting ? (
        <View style={styles.demo} testID="demo-phone">
          <Text variant="badge" tone="secondary">{t('DEMO')}</Text>
          <Text variant="body" align="center">
            {t('No real phone in the demo. Choose what the donor does:')}
          </Text>
          <View style={styles.demoButtons}>
            <Button
              label={t('Decline')}
              variant="secondary"
              onPress={() => void onDecide(false)}
              disabled={decide.isPending}
              fullWidth={false}
              testID="demo-decline"
            />
            <Button
              label={t('Approve')}
              onPress={() => void onDecide(true)}
              loading={decide.isPending}
              fullWidth={false}
              testID="demo-approve"
            />
          </View>
          {decide.error ? (
            <Text variant="caption" tone="danger" align="center">
              {decide.error.message}
            </Text>
          ) : null}
        </View>
      ) : null}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.lg,
  },
  logo: {
    width: 56,
    height: 56,
  },
  glyph: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  summary: {
    flex: 1,
    gap: space.xxs,
  },
  action: {
    marginTop: space.lg,
  },
  footer: {
    gap: space.sm,
  },
  test: {
    marginTop: space.lg,
    padding: space.md,
    gap: space.xs,
    alignItems: 'center',
    borderRadius: radius.lg,
    backgroundColor: colors.bg.brandSubtle,
  },
  waiting: {
    marginTop: space['2xl'],
    alignItems: 'center',
    gap: space.md,
  },
  demo: {
    marginTop: space['2xl'],
    padding: space.lg,
    gap: space.md,
    alignItems: 'center',
    borderRadius: radius.lg,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surfaceSunken,
  },
  demoButtons: {
    flexDirection: 'row',
    gap: space.md,
  },
});
